import { isUndefined, merge } from 'lodash-es';
import type { PropsWithChildren } from 'react';

import { DEFAULT_TRUNCATION_MODE } from '../../core/constants.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  ColorPalette,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import { Formatter } from '../../core/types/formatter.js';
import { ChartToolbarProps } from '../../core/types/toolbar.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { THRESHOLD_TYPE_OPTION } from '../constants.js';
import { defaultConfig } from '../types/default.js';
import {
  DonutChartSlots,
  GroupConfig,
  PieChartContextConfig,
  PieChartLegendConfig,
  PieChartProps,
  PieChartSlots,
  PieDonutConfig,
} from '../types/pie-chart.config.js';
import { PieChartToolbarConfig } from '../types/toolbar.js';
import { isValidThresholdType } from '../utils/is-valid-threshold-type.js';

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
export function getColorPaletteConfiguration(
  contextColorPalette?: ColorPalette | CustomColorPalette,
  propsColorPalette?: ColorPalette | CustomColorPalette,
): ColorPalette | CustomColorPalette {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? defaultConfig.colorPalette;

  return isCustomColorPalette(colorPalette) || isValidColorPalette(colorPalette)
    ? colorPalette
    : defaultConfig.colorPalette;
}

/**
 * Builds a GroupConfig based on context and slots
 *
 * @param contextGrouping - grouping config in context
 * @param slotsGrouping - grouping config in slots
 */
export function getGroupingConfiguration(
  contextGrouping?: GroupConfig,
  slotsGrouping?: Partial<GroupConfig>,
): GroupConfig {
  let grouping = merge(
    {},
    defaultConfig.grouping,
    contextGrouping,
    slotsGrouping,
  );

  grouping = {
    ...grouping,
    threshold: {
      ...grouping.threshold,
      type: isValidThresholdType(grouping.threshold.type)
        ? grouping.threshold.type
        : THRESHOLD_TYPE_OPTION.Relative,
    },
  };

  return grouping;
}

/**
 * Builds a LegendConfiguration configuration based on context and props
 *
 * @param contextLegendConfig - legend config context
 * @param slotsLegendConfig - legend config props
 */
export function getLegendConfiguration(
  contextLegendConfig?: PieChartLegendConfig,
  slotsLegendConfig?: PieChartLegendConfig,
): PieChartLegendConfig {
  let legend = merge(
    {},
    defaultConfig.legend,
    contextLegendConfig,
    slotsLegendConfig,
  );

  const position =
    legend.position && isValidLegendPosition(legend.position)
      ? legend.position
      : defaultConfig.legend.position;

  legend = {
    ...legend,
    position,
  };

  return legend;
}

const validateToolbarContextConfig = (
  contextConfig: PieChartToolbarConfig | undefined,
  // oxlint-disable-next-line typescript/no-deprecated
) => !isUndefined(contextConfig) && contextConfig.downloadData?.enabled;

const getToolbarConfiguration = (
  contextConfig?: PieChartToolbarConfig | undefined,
  slots?: Partial<ChartToolbarProps> | undefined,
) => {
  const hasValidToolbarConfig =
    validateToolbarContextConfig(contextConfig) || !isUndefined(slots);

  return hasValidToolbarConfig ? merge({}, contextConfig, slots) : undefined;
};

/**
 * Build pie chart configuration.
 *
 * @param props - config from props
 * @param contextConfig - config from context
 * @param slots - config from components slots
 * @returns RequiredPieChartConfiguration
 */
export function buildPieChartConfig(
  props: PropsWithChildren<PieChartProps>,
  contextConfig: PieChartContextConfig,
  slots: PieChartSlots | DonutChartSlots,
): PieDonutConfig {
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    props.colorPalette,
  );

  const grouping = getGroupingConfiguration(
    contextConfig.grouping,
    slots.grouping,
  );

  const legend = getLegendConfiguration(contextConfig.legend, slots.legend);

  // The unit lives at the root in the unified `ChartData` format (where
  // `props.data` is an array) and nested in the data object in the legacy format.
  const resolvedUnit = Array.isArray(props.data) ? props.unit : props.data.unit;

  const formatter =
    props.formatter ??
    contextConfig.formatter ??
    (defaultUnitFormatter(resolvedUnit, 0) as Formatter);

  const truncationMode =
    props.truncationMode ??
    contextConfig.truncationMode ??
    DEFAULT_TRUNCATION_MODE;

  const toolbar = getToolbarConfiguration(contextConfig.toolbar, slots.toolbar);

  const seriesActions =
    props.seriesActions ?? contextConfig.seriesActions ?? undefined;

  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);

  return {
    height: props.height ?? defaultConfig.height,
    colorPalette,
    labels: merge({}, defaultConfig.labels, contextConfig.labels, slots.labels),
    grouping,
    legend,
    formatter,
    truncationMode,
    toolbar,
    innerElement: slots.innerElement,
    seriesActions,
    colorRules,
    tooltip: slots.tooltip ?? contextConfig.tooltip,
  };
}

/**
 * Get chart configuration merging the default one with the stringify json config.
 * In case there is a conflict property, default would prevail.
 * In case there is any parse error. It will return an empty config.
 *
 * @param serializedConfig - stringify json config
 * @returns - PieChartConfiguration
 */
export function deserializeConfig(
  serializedConfig: string,
): PieChartContextConfig {
  try {
    return merge({}, defaultConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultConfig;
  }
}
